import { Component, Input, NgZone, OnChanges, OnDestroy, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { cn } from '../../../../utils/utils';

type IfeedbackType = 'success' | 'info' | 'warning' | 'error';

/** Confirmations clear themselves; problems stay until read or dismissed. */
const VISIBLE_FOR: Record<IfeedbackType, number> = {
  success: 5000,
  info: 5000,
  warning: 50000,
  error: 50000,
};

/**
 * Floating notice at the top of its scroll area: a status dot and plain text
 * on the panel colour. Colour lives only in the dot. Hovering or focusing the
 * notice pauses its timer.
 *
 * New code should prefer ToastService (services/toast); this stays for the
 * pages that already render it.
 */
@Component({
  selector: 'feedback-card',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './feedback-card.component.html',
  styleUrl: './feedback-card.component.css',
})
export class FeedbackCardComponent implements OnChanges, OnDestroy {
  private zone = inject(NgZone);
  merge = cn;

  @Input({ required: true }) feedBackType: IfeedbackType = 'success';
  @Input({ required: true }) message: string = '';
  @Input() withTitle: boolean = false;
  showFeedBack: boolean = false;
  @Input() title: string = '';
  @Input() cardClass: string = '';
  private timeoutId: ReturnType<typeof setTimeout> | undefined;
  private remaining = 0;
  private startedAt = 0;

  /** Errors go to the alert region, everything else to the polite status region. */
  get isError(): boolean {
    return this.feedBackType === 'error';
  }

  get heading(): string {
    if (this.title) return this.title;
    switch (this.feedBackType) {
      case 'success':
        return 'Done';
      case 'warning':
        return 'Check this';
      case 'error':
        return 'Something went wrong';
      default:
        return 'Note';
    }
  }

  ngOnChanges() {
    // Pages keep the card mounted with an empty message between notices;
    // only a real message shows and starts the clock.
    if (!this.message) {
      this.showFeedBack = false;
      this.clearTimer();
      return;
    }
    this.showFeedBack = true;
    this.remaining = VISIBLE_FOR[this.feedBackType] ?? VISIBLE_FOR.info;
    this.startTimer();
  }

  pause() {
    if (!this.timeoutId) return;
    clearTimeout(this.timeoutId);
    this.timeoutId = undefined;
    this.remaining = Math.max(1000, this.remaining - (Date.now() - this.startedAt));
  }

  resume() {
    if (this.showFeedBack && this.message && !this.timeoutId) this.startTimer();
  }

  closeFeedback() {
    this.showFeedBack = false;
    this.clearTimer();
  }

  ngOnDestroy() {
    this.clearTimer();
  }

  private startTimer() {
    this.clearTimer();
    // Nothing to time on the server; it would only hold up rendering.
    if (typeof window === 'undefined') return;
    this.startedAt = Date.now();
    // Outside the zone, so a notice on screen never keeps the app from settling.
    this.timeoutId = this.zone.runOutsideAngular(() =>
      setTimeout(
        () =>
          this.zone.run(() => {
            this.showFeedBack = false;
            this.timeoutId = undefined;
          }),
        this.remaining
      )
    );
  }

  private clearTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }
}
